import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isOnline, syncConversationsToOffline } from "@/utils/offlineStorage";
import { Message } from "@/types/chat";
import { DB_TABLES } from "@/constants";

const MAX_SELECTED_DOCUMENTS = 20;

export const useConversations = () => {
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const loadConversation = useCallback(async (conversationId: string) => {
    // Validate conversation ID
    if (!conversationId || typeof conversationId !== "string") {
      console.error("Invalid conversation ID:", conversationId);
      return [];
    }
    
    // Prevent concurrent loads
    if (isLoading) {
      return [];
    }
    
    setIsLoading(true);
    
    try {
      // Fetch conversation and messages in parallel for speed
      const [convResult, messagesResult] = await Promise.all([
        supabase
          .from(DB_TABLES.CONVERSATIONS)
          .select("selected_documents")
          .eq("id", conversationId)
          .single(),
        supabase
          .from(DB_TABLES.CHAT_MESSAGES)
          .select("role, content, created_at")
          .eq("conversation_id", conversationId)
          .order("created_at", { ascending: true })
          .limit(100)
      ]);
      
      // Handle conversation data
      if (convResult.error) {
        throw convResult.error;
      }
      
      if (convResult.data) {
        const selectedDocs = convResult.data.selected_documents as string[] | null;
        console.log("📄 [useConversations] Loaded conversation documents:", selectedDocs);
        console.log("📄 [useConversations] Document count:", selectedDocs?.length || 0);
        if (selectedDocs && selectedDocs.length > 0) {
          if (selectedDocs.length > MAX_SELECTED_DOCUMENTS) {
            setSelectedDocumentIds(selectedDocs.slice(0, MAX_SELECTED_DOCUMENTS));
            toast({
              title: "Document limit applied",
              description: `This conversation had ${selectedDocs.length} documents. Only the first ${MAX_SELECTED_DOCUMENTS} were loaded.`,
            });
          } else {
            setSelectedDocumentIds(selectedDocs);
          }
        } else {
          setSelectedDocumentIds([]);
        }
      }
      
      // Handle messages data
      if (messagesResult.error) {
        throw messagesResult.error;
      }
      
      const typedMessages = (messagesResult.data || []).map(msg => ({
        role: msg.role as "user" | "assistant",
        content: msg.content,
        timestamp: msg.created_at
      }));

      setCurrentConversationId(conversationId);
      return typedMessages;
    } catch (error) {
      console.error("Error loading conversation:", error);
      toast({
        title: "Error",
        description: "Failed to load conversation",
        variant: "destructive",
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const createNewConversation = useCallback(async (
    firstMessage: string,
    userId: string,
    selectedDocs: string[]
  ): Promise<string | null> => {
    try {
      const title = firstMessage.slice(0, 50) + (firstMessage.length > 50 ? "..." : "");
      
      const { data, error } = await supabase
        .from(DB_TABLES.CONVERSATIONS)
        .insert({
          user_id: userId,
          title,
          selected_documents: selectedDocs,
        })
        .select()
        .single();

      if (error) throw error;
      
      setCurrentConversationId(data.id);
      return data.id;
    } catch (error) {
      console.error("Error creating conversation:", error);
      toast({
        title: "Error",
        description: "Failed to create conversation",
        variant: "destructive",
      });
      return null;
    }
  }, [toast]);

  const saveMessage = useCallback(async (
    conversationId: string,
    role: "user" | "assistant",
    content: string | any // Allow both string and structured content
  ) => {
    try {
      // If content is an array (contains images), stringify it
      const contentToSave = Array.isArray(content) ? JSON.stringify(content) : content;
      
      const { error } = await supabase
        .from(DB_TABLES.CHAT_MESSAGES)
        .insert({
          conversation_id: conversationId,
          role,
          content: contentToSave,
        });

      if (error) throw error;

      // Sync to offline storage after saving
      if (isOnline()) {
        syncConversationsToOffline();
      }
    } catch (error) {
      console.error("Error saving message:", error);
      toast({
        title: "Error",
        description: "Failed to save message",
        variant: "destructive",
      });
    }
  }, [toast]);

  const updateConversationDocuments = useCallback(async (
    conversationId: string,
    documentIds: string[]
  ) => {
    try {
      const { error } = await supabase
        .from(DB_TABLES.CONVERSATIONS)
        .update({ selected_documents: documentIds })
        .eq("id", conversationId);

      if (error) throw error;
    } catch (error) {
      console.error("Error updating conversation documents:", error);
    }
  }, []);

  const resetConversation = useCallback(() => {
    setCurrentConversationId(null);
  }, []);

  return {
    currentConversationId,
    setCurrentConversationId,
    selectedDocumentIds,
    setSelectedDocumentIds,
    loadConversation,
    createNewConversation,
    saveMessage,
    updateConversationDocuments,
    resetConversation,
  };
};
